import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, euro } from "../lib/api";
import {
  Sparkles, ArrowRight, ArrowLeft, Share2, RefreshCw, AlertTriangle,
  TrendingDown, Award, Target, Eye, EyeOff, Mail, Check,
} from "lucide-react";
import { toast } from "sonner";

const SHARE_URL = typeof window !== "undefined" ? window.location.origin : "";

function scoreFromData(d) {
  if (!d) return 0;
  let s = 60;
  const tva = d.tva_progress ?? 0;
  const rate = d.rate ?? d.urssaf_rate ?? 0.256;
  const monthly = d.monthly_revenue ?? 0;
  // Pénalité TVA proche du seuil
  if (tva > 95) s -= 25;
  else if (tva > 80) s -= 15;
  else if (tva > 60) s -= 5;
  // Bonus si CA mensuel > 2500 (lucidité du marché)
  if (monthly >= 2500) s += 15;
  else if (monthly >= 1500) s += 8;
  else if (monthly < 800) s -= 10;
  // Pénalité légère si PFL pas utilisé alors qu'il aurait pu réduire (info-only)
  if (rate >= 0.27) s += 5; // a choisi PFL = lucide
  s = Math.max(15, Math.min(100, s));
  return Math.round(s);
}

function profileFromScore(score) {
  if (score >= 80) return { label: "Vous êtes au top de votre pilotage", color: "var(--sage)", emoji: "🌿" };
  if (score >= 60) return { label: "Vous êtes lucide, mais vous pouvez optimiser", color: "#7B8C7A", emoji: "🌱" };
  if (score >= 40) return { label: "Vous pilotez à l'aveugle, et ça vous coûte", color: "#B58B5C", emoji: "🌫️" };
  return { label: "Urgence : reprenez les commandes maintenant", color: "#A04E3A", emoji: "🚨" };
}

export default function Diagnostic() {
  const [step, setStep] = useState(1);
  const [monthly, setMonthly] = useState("");
  const [regime, setRegime] = useState("SSI");
  const [pfl, setPfl] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/simulate", {
        monthly_revenue: Number(monthly) || 0,
        regime,
        pfl,
      });
      setResult({ ...data, monthly_revenue: Number(monthly) || 0 });
      setStep(4);
    } catch (e) {
      toast.error("Erreur de calcul, réessayez.");
    }
    setLoading(false);
  };

  const restart = () => {
    setStep(1);
    setMonthly("");
    setRegime("SSI");
    setPfl(false);
    setResult(null);
  };

  const share = async () => {
    const score = scoreFromData(result);
    const profile = profileFromScore(score);
    const message =
      `Je viens de découvrir mon score de pilotage financier sur Mon Cabinet Bien-être : ${score}/100 ${profile.emoji}.\n` +
      `${profile.label}\n\n` +
      `Et toi, où en es-tu ? Le test est gratuit (30s) :\n${SHARE_URL}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Mon score de pilotage", text: message, url: SHARE_URL }); }
      catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(message);
        toast.success("Message copié ! Collez-le où vous voulez (WhatsApp, Email…)");
      } catch { toast.error("Impossible de copier."); }
    }
  };

  return (
    <section
      className="px-6 sm:px-10 max-w-[1100px] mx-auto py-12 sm:py-16"
      data-testid="diagnostic-section"
      id="diagnostic"
    >
      <div className="card-soft p-6 sm:p-10 lg:p-14" style={{ background: "linear-gradient(135deg, var(--surface) 0%, var(--sage-light) 100%)" }}>
        {step !== 4 && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur mb-4">
              <Sparkles className="w-3.5 h-3.5 text-sage" strokeWidth={1.6} />
              <span className="text-xs text-sage font-medium">Gratuit · 30 secondes · sans inscription</span>
            </div>
            <ProgressDots step={step} />
          </div>
        )}

        {step === 1 && (
          <Step
            title="Quel est votre régime social ?"
            subtitle="Cela change radicalement vos cotisations URSSAF."
            testid="diagnostic-step-1"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => { setRegime("SSI"); setStep(2); }}
                className={`card-soft card-soft-hover p-6 text-left ${regime === "SSI" ? "ring-2 ring-sage" : ""}`}
                data-testid="regime-ssi"
              >
                <div className="font-display text-lg mb-1">SSI</div>
                <div className="text-sm text-muted-soft">Profession libérale non réglementée hors Cipav (sophrologue, naturopathe, hypnothérapeute, énergéticien, coach bien-être…)</div>
                <div className="text-xs mt-3 font-medium text-sage">Taux 25,6 % du CA</div>
              </button>
              <button
                onClick={() => { setRegime("CIPAV"); setStep(2); }}
                className={`card-soft card-soft-hover p-6 text-left ${regime === "CIPAV" ? "ring-2 ring-sage" : ""}`}
                data-testid="regime-cipav"
              >
                <div className="font-display text-lg mb-1">Cipav</div>
                <div className="text-sm text-muted-soft">Profession relevant de la Cipav (ostéopathe DO, psychomotricien, professions libérales réglementées historiques…)</div>
                <div className="text-xs mt-3 font-medium text-sage">Taux 23,2 % du CA</div>
              </button>
            </div>
            <div className="text-[11px] text-muted-soft mt-3 leading-relaxed text-center">
              Le régime dépend de votre activité et de votre situation. Vérifiez-le sur votre attestation URSSAF si vous avez un doute.
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step
            title="Combien facturez-vous en moyenne par mois ?"
            subtitle="Votre chiffre d'affaires brut (avant URSSAF, avant tout)."
            testid="diagnostic-step-2"
          >
            <div className="max-w-sm mx-auto">
              <div className="relative">
                <input
                  type="number"
                  inputMode="numeric"
                  className="input-soft text-3xl font-display text-center"
                  style={{ height: 90 }}
                  placeholder="2 500"
                  value={monthly}
                  onChange={(e) => setMonthly(e.target.value)}
                  autoFocus
                  data-testid="monthly-input"
                />
                <div className="absolute right-5 top-1/2 -translate-y-1/2 text-2xl text-muted-soft">€</div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4">
                {[1500, 2500, 3500, 5000].map((v) => (
                  <button
                    key={v}
                    onClick={() => setMonthly(String(v))}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${String(monthly) === String(v) ? "bg-sage text-white border-sage" : "bg-white hover:bg-sage-light border-transparent"}`}
                    data-testid={`preset-${v}`}
                  >
                    {v} €
                  </button>
                ))}
              </div>
            </div>
            <Nav onBack={() => setStep(1)} onNext={() => setStep(3)} disabled={!monthly || Number(monthly) <= 0} />
          </Step>
        )}

        {step === 3 && (
          <Step
            title="Avez-vous activé l'option PFL ?"
            subtitle="Le Versement Forfaitaire Libératoire (PFL) paye l'impôt sur le revenu en même temps que les cotisations. +2,2 % du CA."
            testid="diagnostic-step-3"
          >
            <div className="grid sm:grid-cols-2 gap-3 max-w-lg mx-auto">
              <button
                onClick={() => { setPfl(false); submit(); }}
                className="card-soft card-soft-hover p-6 text-left"
                data-testid="pfl-no"
              >
                <EyeOff className="w-6 h-6 text-muted-soft mb-2" strokeWidth={1.5} />
                <div className="font-display text-lg mb-1">Non, pas d'option PFL</div>
                <div className="text-sm text-muted-soft">Je paie l'IR séparément via ma déclaration annuelle</div>
              </button>
              <button
                onClick={() => { setPfl(true); submit(); }}
                className="card-soft card-soft-hover p-6 text-left"
                data-testid="pfl-yes"
              >
                <Eye className="w-6 h-6 text-sage mb-2" strokeWidth={1.5} />
                <div className="font-display text-lg mb-1">Oui, PFL activé</div>
                <div className="text-sm text-muted-soft">L'impôt sort en même temps que les cotisations (libératoire)</div>
              </button>
              <button
                onClick={() => { setPfl(false); submit(); }}
                className="sm:col-span-2 text-xs text-muted-soft hover:text-sage underline mt-2"
                data-testid="pfl-skip"
              >
                Je ne sais pas → continuer sans PFL
              </button>
            </div>
            {loading && <div className="text-center text-sage mt-6">Calcul en cours…</div>}
            <div className="flex justify-start mt-6">
              <button onClick={() => setStep(2)} className="btn-ghost text-sm" data-testid="back-step-3">
                <ArrowLeft className="inline w-4 h-4 mr-1.5" /> Retour
              </button>
            </div>
          </Step>
        )}

        {step === 4 && result && (
          <Result result={result} regime={regime} pfl={pfl} onRestart={restart} onShare={share} />
        )}
      </div>
    </section>
  );
}

function ProgressDots({ step }) {
  return (
    <div className="flex justify-center gap-2">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-1.5 rounded-full transition-all"
          style={{
            width: i === step ? 32 : 16,
            backgroundColor: i <= step ? "var(--sage)" : "var(--sage-light)",
          }}
        />
      ))}
    </div>
  );
}

function Step({ title, subtitle, children, testid }) {
  return (
    <div data-testid={testid}>
      <h2 className="font-display text-2xl sm:text-3xl text-center mb-2">{title}</h2>
      <p className="text-sm text-muted-soft text-center mb-8 max-w-xl mx-auto">{subtitle}</p>
      {children}
    </div>
  );
}

function Nav({ onBack, onNext, disabled }) {
  return (
    <div className="flex justify-between mt-8">
      <button onClick={onBack} className="btn-ghost text-sm" data-testid="step-back">
        <ArrowLeft className="inline w-4 h-4 mr-1.5" /> Retour
      </button>
      <button onClick={onNext} disabled={disabled} className="btn-sage text-sm" data-testid="step-next">
        Continuer <ArrowRight className="inline w-4 h-4 ml-1.5" />
      </button>
    </div>
  );
}

function Result({ result, regime, pfl, onRestart, onShare }) {
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureName, setCaptureName] = useState("");
  const [captureConsent, setCaptureConsent] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [captured, setCaptured] = useState(false);

  const monthly = result.monthly_revenue;
  const urssaf = result.monthly_urssaf ?? result.urssaf_estimate ?? 0;
  const net = result.monthly_after_charges ?? result.after_charges ?? 0;
  const annualBrut = monthly * 12;
  const annualNet = result.yearly_after_charges ?? (net * 12);
  const score = scoreFromData(result);
  const profile = profileFromScore(score);
  const tvaProgress = result.tva_progress || 0;

  // Combien il faudrait facturer pour avoir 3000 € net
  const targetForGoal = (goal) => (goal / (net / monthly)).toFixed(0);

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!captureEmail) return;
    setCapturing(true);
    try {
      await api.post("/leads/capture", {
        email: captureEmail,
        full_name: captureName,
        score, profile_label: profile.label,
        monthly_revenue: monthly,
        urssaf_estimate: urssaf,
        after_charges: net,
        regime, pfl,
        consent: captureConsent,
      });
      setCaptured(true);
      toast.success("✅ Bilan envoyé par email — vérifiez votre boîte (et les spams)");
      // Meta: Lead conversion event
      try {
        const { trackMetaEvent } = await import("../lib/metaPixel");
        await trackMetaEvent("Lead", {
          content_name: "URSSAF diagnostic",
          value: 0,
          currency: "EUR",
        }, { email: captureEmail });
      } catch (_) { /* never break UX */ }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur d'envoi. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur d'envoi");
    }
    setCapturing(false);
  };

  return (
    <div data-testid="diagnostic-result">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur mb-3">
          <Award className="w-3.5 h-3.5 text-sage" strokeWidth={1.8} />
          <span className="text-xs font-medium" style={{ color: profile.color }}>Votre diagnostic est prêt</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl mb-2">Voici ce que vous ne voyez pas encore.</h2>
      </div>

      {/* Score circle */}
      <div className="flex flex-col items-center mb-8">
        <ScoreCircle score={score} color={profile.color} />
        <div className="text-center mt-4">
          <div className="text-3xl mb-1">{profile.emoji}</div>
          <div className="font-display text-xl" style={{ color: profile.color }}>{profile.label}</div>
        </div>
      </div>

      {/* Vérité financière */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.05)] mb-6">
        <div className="text-xs uppercase tracking-wider text-muted-soft mb-4 text-center">📊 Votre vérité financière</div>
        <div className="space-y-4">
          <Row label="💼 Ce que vous croyez gagner" value={euro(monthly)} sub="CA brut mensuel" />
          <Row label="✂️ URSSAF vous prendra" value={"− " + euro(urssaf)} sub={`Taux ${((result.rate ?? result.urssaf_rate ?? 0) * 100).toFixed(1).replace(".", ",")}% (${regime}${pfl ? " + PFL" : ""})`} clay />
          <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
            <Row label="💰 Vous gagnez réellement" value={euro(net)} sub="Net mensuel" big />
          </div>
        </div>

        {tvaProgress > 80 && (
          <div className="mt-5 p-3 rounded-xl flex items-start gap-2" style={{ backgroundColor: "#FBEEE8", color: "#8B4D3D" }}>
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <b>⚠️ Alerte TVA</b> — à ce rythme vous frôlerez le seuil de franchise (37 500 €/an).
              Au-delà : facturation TVA obligatoire et 8 312 € de perte annuelle nette.
            </div>
          </div>
        )}
      </div>

      {/* La prise de conscience */}
      {net > 0 && (
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.05)] mb-6">
          <div className="text-xs uppercase tracking-wider text-muted-soft mb-4 text-center">🎯 Pour atteindre vos objectifs</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <GoalCard label="2 000 € net / mois" value={`${targetForGoal(2000)} € à facturer`} />
            <GoalCard label="3 000 € net / mois" value={`${targetForGoal(3000)} € à facturer`} highlight />
            <GoalCard label="4 000 € net / mois" value={`${targetForGoal(4000)} € à facturer`} />
          </div>
          <p className="text-xs text-muted-soft text-center mt-4">
            Sur l'année, votre net actuel = <b style={{ color: "var(--sage)" }}>{euro(annualNet)}</b> (CA {euro(annualBrut)} − URSSAF {euro(annualBrut * (result.rate ?? result.urssaf_rate ?? 0))})
          </p>
        </div>
      )}

      {/* Email capture — récupération du lead AVANT le CTA payant */}
      {!captured ? (
        <form onSubmit={submitEmail} className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.05)] mb-6" data-testid="lead-capture-form">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-sage" strokeWidth={1.8} />
            <div className="font-display text-lg">Envoyez-moi ce bilan + 3 leviers pour augmenter mon net</div>
          </div>
          <p className="text-sm text-muted-soft mb-4">
            Vous recevrez votre bilan personnalisé par email, suivi de 3 conseils concrets pour passer de {Math.round(net)} € à plus.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input
              type="text" placeholder="Votre prénom (optionnel)"
              className="input-soft" value={captureName}
              onChange={(e) => setCaptureName(e.target.value)}
              data-testid="lead-name"
            />
            <input
              type="email" placeholder="votre.email@exemple.fr" required
              className="input-soft" value={captureEmail}
              onChange={(e) => setCaptureEmail(e.target.value)}
              data-testid="lead-email"
            />
          </div>
          <label className="flex items-start gap-2.5 text-xs text-muted-soft mb-4 cursor-pointer">
            <input
              type="checkbox" checked={captureConsent}
              onChange={(e) => setCaptureConsent(e.target.checked)}
              className="mt-0.5 accent-sage" data-testid="lead-consent"
            />
            <span>
              J'accepte de recevoir mon bilan + 3 conseils par email. Conformément au RGPD, je peux me désinscrire à tout moment via le lien en bas des emails. Vos données ne sont jamais revendues.
            </span>
          </label>
          <button
            type="submit" disabled={!captureEmail || !captureConsent || capturing}
            className="btn-sage text-sm w-full sm:w-auto"
            data-testid="lead-submit"
          >
            {capturing ? "Envoi en cours…" : "📩 Envoyer mon bilan par email"}
          </button>
        </form>
      ) : (
        <div className="bg-sage-light rounded-2xl p-6 text-center mb-6" data-testid="lead-captured">
          <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display text-lg mb-1">Bilan envoyé ✨</div>
          <div className="text-sm text-muted-soft">Vérifiez votre boîte de réception (et les indésirables / spams).</div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-sage text-white rounded-2xl p-6 sm:p-10 text-center shadow-[0_20px_50px_rgba(123,140,122,0.4)] mb-4">
        <div className="font-display text-2xl sm:text-3xl mb-3 leading-tight">
          Vous voulez voir <u>en temps réel</u><br />ce qu'il vous reste vraiment ?
        </div>
        <p className="text-white/90 mb-6 max-w-xl mx-auto">
          Mon Cabinet Bien-être calcule pour vous chaque mois votre URSSAF, vos charges,
          vos factures, et vous montre votre <b>Net réel</b>. Sans calcul, sans Excel, sans comptable.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-white text-sage font-medium px-8 py-3.5 rounded-2xl hover:bg-beige transition-all"
          data-testid="diagnostic-cta-register"
        >
          S'abonner — Garantie 14 jours satisfait ou remboursé <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="text-xs text-white/80 mt-4">19,99 € TTC/mois · sans engagement · résiliable à tout moment</div>
      </div>

      {/* Share + restart */}
      <div className="flex flex-wrap gap-3 justify-center">
        <button onClick={onShare} className="btn-ghost text-sm" data-testid="diagnostic-share">
          <Share2 className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} /> Partager mon score
        </button>
        <button onClick={onRestart} className="btn-ghost text-sm" data-testid="diagnostic-restart">
          <RefreshCw className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} /> Refaire le test
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, sub, big, clay }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="text-xs text-muted-soft mt-0.5">{sub}</div>}
      </div>
      <div
        className={big ? "font-display text-3xl text-sage" : "font-display text-xl"}
        style={clay ? { color: "#A04E3A" } : {}}
      >
        {value}
      </div>
    </div>
  );
}

function GoalCard({ label, value, highlight }) {
  return (
    <div
      className="rounded-xl p-4 text-center"
      style={{
        backgroundColor: highlight ? "var(--sage)" : "var(--sage-light)",
        color: highlight ? "white" : "inherit",
      }}
    >
      <div className={highlight ? "text-xs text-white/80" : "text-xs text-muted-soft"}>{label}</div>
      <div className="font-display text-lg mt-1">{value}</div>
    </div>
  );
}

function ScoreCircle({ score, color }) {
  const r = 78;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative" style={{ width: 180, height: 180 }}>
      <svg width="180" height="180" viewBox="0 0 180 180">
        <circle cx="90" cy="90" r={r} fill="none" stroke="var(--sage-light)" strokeWidth="14" />
        <circle
          cx="90" cy="90" r={r} fill="none" stroke={color} strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 90 90)"
          style={{ transition: "stroke-dashoffset 1.5s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-5xl" style={{ color }}>{score}</div>
        <div className="text-xs text-muted-soft uppercase tracking-wider">sur 100</div>
      </div>
    </div>
  );
}
